import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import MainLayout from "@/components/MainLayout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "./pages/Home";
import { useFavicon } from "@/hooks/useFavicon";

// Lazy load non-critical routes
const Menu = lazy(() => import("./pages/Menu"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ManagementLogin = lazy(() => import("./pages/ManagementLogin"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Reservations = lazy(() => import("./pages/Reservations"));
const Catering = lazy(() => import("./pages/Catering"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RewardsSignup = lazy(() => import("./pages/RewardsSignup"));

const queryClient = new QueryClient();

// Component to handle favicon inside the provider tree
const FaviconHandler = () => {
  useFavicon();
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <LanguageProvider>
            <BrowserRouter>
            <FaviconHandler />
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Dashboard - standalone with sidebar */}
                <Route path="/dashboard/*" element={<Dashboard />} />
                
                {/* Main app routes with Navigation/Footer */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/menu" element={<Menu />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/reservations" element={<Reservations />} />
                  <Route path="/catering" element={<Catering />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/rewards" element={<RewardsSignup />} />
                  <Route path="/management" element={<ManagementLogin />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
            <Toaster />
            <Sonner />
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
